import React from 'react';

import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import {
  DangerButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import ShieldExclamationIcon from '@grnhse/seedling/lib/birch/icons/shield_exclamation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';

import { withLocalePrefix } from 'shared/utils/translation';
import { useFraudReportStatus } from 'fraud/hooks/useFraudReportStatus';
import { FraudReport } from '../types';

const t = withLocalePrefix('fraud.fraud_report.actions');

const FraudStatusBannerActions = ({
  canRunFraudReports,
  fraudReport,
  openFullReportModal,
  onRunFraudReport,
  onRetryFailedFraudReport,
  onRestoreFraudReport,
  onResolveFraudReport,
  onRejectAsSecurityConcern,
  canRejectAsSecurityConcern,
  canReject,
  isActiveApplication = true,
}: {
  canRunFraudReports: boolean;
  fraudReport: FraudReport | null;
  openFullReportModal: () => void;
  onRunFraudReport: () => void;
  onRetryFailedFraudReport: () => void;
  onRestoreFraudReport?: () => void;
  onResolveFraudReport?: () => void;
  onRejectAsSecurityConcern?: () => void;
  canRejectAsSecurityConcern: boolean;
  canReject?: boolean;
  isActiveApplication?: boolean;
}) => {
  const {
    isReportSuccess,
    isReportPending,
    isReportFailed,
    isReportInsufficientData,
    isReportResolved,
  } = useFraudReportStatus(fraudReport);

  const showViewReportButton = isReportSuccess;
  const showRunReportButton =
    canRunFraudReports &&
    isActiveApplication &&
    !isReportSuccess &&
    !isReportPending &&
    !isReportFailed &&
    !isReportInsufficientData;
  const showRetryFailedReportButton = canRunFraudReports && isReportFailed;

  const hasFraudSignals = (fraudReport?.fraud_signals?.length || 0) > 0;
  const showResolveSignalsButton =
    canRunFraudReports &&
    isReportSuccess &&
    !isReportResolved &&
    !!onResolveFraudReport &&
    hasFraudSignals;

  const showRestoreSignalsButton =
    canRunFraudReports && isReportResolved && !!onRestoreFraudReport;

  let showRejectAsSecurityConcernButton: boolean;
  if (!!fraudReport?.resolved_at) {
    showRejectAsSecurityConcernButton = false;
  } else {
    showRejectAsSecurityConcernButton =
      (canRejectAsSecurityConcern &&
        canReject &&
        fraudReport?.status === 'success') ??
      false;
  }

  if (
    !showViewReportButton &&
    !showRunReportButton &&
    !showRetryFailedReportButton &&
    !showResolveSignalsButton &&
    !showRestoreSignalsButton &&
    !showRejectAsSecurityConcernButton
  ) {
    return null;
  }

  return (
    <Stack spacing={3} data-provides="fraud-status-actions">
      {showRunReportButton && (
        <Grid sx={{ marginY: 1 }}>
          <SecondaryButton
            data-provides="run-report-button"
            onClick={onRunFraudReport}
          >
            {t('run_report')}
          </SecondaryButton>
        </Grid>
      )}

      {showRetryFailedReportButton && (
        <Grid sx={{ marginY: 1 }}>
          <SecondaryButton
            data-provides="rerun-report-button"
            onClick={onRetryFailedFraudReport}
          >
            {t('rerun_report')}
          </SecondaryButton>
        </Grid>
      )}

      {(showRejectAsSecurityConcernButton ||
        showResolveSignalsButton ||
        showRestoreSignalsButton) && (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          spacing={1.5}
        >
          {showRejectAsSecurityConcernButton && (
            <Grid>
              <DangerButton
                data-provides="reject-as-security-concern-button"
                onClick={onRejectAsSecurityConcern}
                icon={ShieldExclamationIcon}
                iconPosition="left"
                size="md"
              >
                {t('reject_as_security_concern')}
              </DangerButton>
            </Grid>
          )}
          {showRestoreSignalsButton && (
            <Grid>
              <SecondaryButton
                data-provides="restore-signals-button"
                onClick={onRestoreFraudReport}
                size="md"
              >
                {t('restore_signals')}
              </SecondaryButton>
            </Grid>
          )}
          {showResolveSignalsButton && (
            <Grid>
              <SecondaryButton
                data-provides="resolve-signals-button"
                onClick={onResolveFraudReport}
                size="md"
              >
                {t('resolve_signals')}
              </SecondaryButton>
            </Grid>
          )}
        </Grid>
      )}

      {showViewReportButton && (
        <Grid>
          <SecondaryButton
            size="sm"
            onClick={openFullReportModal}
            data-provides="view-report-button"
          >
            {t('view_report')}
          </SecondaryButton>
        </Grid>
      )}
    </Stack>
  );
};

export default FraudStatusBannerActions;
